import { Box, Button, Chip, CircularProgress, Typography } from "@mui/joy";
import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { CallMasthead } from "../atoms/callMasthead";
import { Paper } from "../atoms/paper";
import { callTheme, interactorLabelColor } from "../entities/callDesign";
import {
  CallPageToc,
  CallPageTocBar,
  ModernInviteContent,
} from "./modernInvite";
import { ConfettiCenterContext } from "../context/confettiCenter/confettiCenter";
import { MissionMap } from "../entities/missionMap";
import { Interactor } from "../entities/missionMap/missionMap";
import { solidIcon, uploadInspectorMessages } from "../helpers";
import {
  detectPartialAcceptance,
  findHoldInteractor,
  isAcceptInteractor,
  typedMessage,
} from "../partialAcceptance";
import { PartialAcceptWarning } from "../organisms/partialAcceptWarning";
import { Contacts } from "../organisms/contact";
import { DressCode } from "../organisms/dressCode";
import { Footer } from "../organisms/footer";
import { Greetings } from "../organisms/greetings";
import { Links } from "../organisms/links";
import { CallMessages } from "../organisms/callMessages";
import { Messages } from "../organisms/messages";
import { ProjectNotes } from "../organisms/projectNotes";
import { ProjectRosters } from "../organisms/projectRosters";
import { Repertoire } from "../organisms/repertoire";
import { Response } from "../organisms/response";
import { Schedule } from "../organisms/schedule";
import { Wage } from "../organisms/wage";
import {
  useAnswerMutation,
  useGetMissionMapQuery,
  useLazyGetMissionMapQuery,
} from "../redux/missionMap";
import {
  inspectorsSelector,
  selectedInteractorJobStageIDSelector,
  selectedInteractorSelector,
  setSelectedInteractor,
  setSelectedInteractorJobStageID,
} from "../redux/rhapsodyReducer";
import { useUploadFileMutation } from "../redux/upload";

export function useMissionMap(token: string): MissionMap | undefined {
  const { data } = useGetMissionMapQuery(token ?? "", { skip: !token });
  return useMemo(() => (data ? new MissionMap(data) : undefined), [data]);
}

/**
 *
 * @returns {ReactElement} A happy coding page!
 */
export function ProjectInvite({
  token,
  hideResponse,
  hideLogo,
  hideFooter,
}: {
  token: string;
  hideLogo?: boolean;
  hideResponse?: boolean;
  hideFooter?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const mainToken = searchParams.get("token") ?? "";
  const mission = useMissionMap(token);
  const selectedInteractor = useSelector(selectedInteractorSelector);
  const selectedInteractorJobStageID = useSelector(
    selectedInteractorJobStageIDSelector,
  );
  const [loading, setLoading] = useState(false);
  const [upload] = useUploadFileMutation();
  const inspectors = useSelector(inspectorsSelector);
  const confettiCenter = useContext(ConfettiCenterContext);
  const dispatch = useDispatch();
  const [answer] = useAnswerMutation();
  const [getMissionMap] = useLazyGetMissionMapQuery();
  // Set when the confirm bar is about to send an acceptance whose message reads
  // like the musician can only play part of the call.
  const [partialAccept, setPartialAccept] = useState<Interactor>();
  const holdInteractor = findHoldInteractor(mission?.Interactors);
  // Modern Calls power-up: white ground + accent masthead when the org has
  // it; the legacy full-bleed company color otherwise. Server-gated — the
  // mapping only carries ModernCall for opted-in orgs.
  const theme = callTheme(mission);

  /**
   * Sends the answer, unless the message reads like a partial acceptance and
   * Hold is on offer — in which case the musician is asked first.
   *
   * This is the last thing between the confirm bar and a sent answer, which is
   * where the check has to live: the message field stays editable while the bar
   * is up, so a caveat typed after pressing Accept would otherwise sail through.
   *
   * @param {Interactor} i The interactor the musician chose.
   */
  const confirm = (i: Interactor) => {
    if (holdInteractor && isAcceptInteractor(i)) {
      if (detectPartialAcceptance(typedMessage(inspectors)).partial)
        return setPartialAccept(i);
    }
    handleSubmit(i);
  };

  useEffect(() => {
    if (selectedInteractor && selectedInteractorJobStageID) {
      handleSubmit(selectedInteractor);
    }
  }, [selectedInteractor, selectedInteractorJobStageID]);

  const handleSubmit = async (i: Interactor) => {
    dispatch(setSelectedInteractor(i));
    setLoading(true);
    try {
      const ins = await uploadInspectorMessages(inspectors, upload);
      if (mission?.PublicToken && mission.PublicToken[0] !== "G") {
        await answer({
          [mission.PublicToken]: {
            mercuryInteractorID: i.ID,
            mercuryInspectors: ins,
            jobStageID: selectedInteractorJobStageID,
          },
        }).unwrap();
        dispatch(setSelectedInteractor());
        dispatch(setSelectedInteractorJobStageID());
        setSearchParams({ stage: i.Name.toLowerCase(), token: token ?? "" });
        if (isAcceptInteractor(i)) {
          let won = true;
          if (mission.IsCattleCall) {
            // First come, first served: the seat may have been claimed by
            // someone else while answering. The refetched public mapping is
            // the source of truth — only celebrate an actual win.
            try {
              const fresh = await getMissionMap(token).unwrap();
              won = fresh?.TooLate !== true;
            } catch {
              won = false;
            }
          }
          if (won) confettiCenter.show();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!mission) return <Box />;

  if (mission.TooLate) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          background: theme.ground,
          alignItems: "center",
          position: "relative",
          flex: 1,
        }}
      >
        <Helmet>
          <meta name="theme-color" content={theme.themeColor} />
          <title>{mission?.CompanyName}</title>
        </Helmet>
        <Box
          sx={{
            maxWidth: 480,
            width: "100%",
            p: 1,
            gap: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          {!hideLogo ? (
            theme.modern ? (
              <CallMasthead
                theme={theme}
                companyName={mission?.CompanyName}
                logoURL={mission?.CallMastheadLogo}
              />
            ) : (
              <img
                src={mission?.CompanyLogo}
                style={{ height: 70, margin: "auto" }}
              />
            )
          ) : (
            []
          )}
          <Paper>
            <Typography level="h2">Sorry, it is too late.</Typography>
            <Typography level="body-sm">
              All the seats for this call have been filled.
            </Typography>
          </Paper>
          {mission?.Contacts?.length ? <Contacts token={token} /> : []}
        </Box>
        {!hideFooter ? <Footer token={token} /> : []}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        background: theme.ground,
        alignItems: "center",
        position: "relative",
        flex: 1,
      }}
    >
      <Helmet>
        <meta name="theme-color" content={theme.themeColor} />
        <title>{mission?.CompanyName}</title>
      </Helmet>
      {/* Legacy keeps its chip bar above the masthead. On a modern call the
          messages moved down into the column (organisms/callMessages) and the
          bar survives only for polls, which are a form with their own card and
          still open in the overlay. */}
      {(
        theme.modern
          ? mission?.Polls?.length
          : mission?.Messages.length || mission?.Polls?.length
      ) ? (
        <Messages token={token} hideMessages={theme.modern} />
      ) : (
        []
      )}
      <Box
        sx={{
          // The modern call mirrors the email's 680px ceiling; legacy keeps
          // its historical 480px column untouched.
          maxWidth: theme.modern ? 680 : 480,
          width: "100%",
          p: theme.modern ? 2 : 1,
          gap: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        {!hideLogo ? (
          theme.modern ? (
            <CallMasthead
              theme={theme}
              companyName={mission?.CompanyName}
              kind={mission?.EventStatusTitle}
              logoURL={mission?.CallMastheadLogo}
            />
          ) : (
            <img
              src={mission?.CompanyLogo}
              style={{ height: 70, margin: "auto" }}
            />
          )
        ) : (
          []
        )}
        {/* Mobile twin of the ToC rail: a sticky collapsible bar in the
            column flow, shown below lg. Standalone page only, like the
            rail. */}
        {theme.modern && !hideResponse && !hideLogo && !hideFooter ? (
          <CallPageTocBar mission={mission} theme={theme} />
        ) : null}
        {theme.modern && mission ? (
          // The modern call page IS the email, block for block. No cards, no
          // legacy organisms — see templates/modernInvite.tsx.
          <ModernInviteContent
            mission={mission}
            theme={theme}
            token={token}
            hideResponse={hideResponse}
            hideFooter={hideFooter}
            messages={
              <CallMessages
                token={token}
                theme={theme}
                hideResponse={hideResponse}
              />
            }
          />
        ) : (
          <>
            {mission !== undefined ? (
              <Greetings hideResponse={hideResponse} token={token} />
            ) : (
              []
            )}
            {mission?.allInteractors()?.length && !hideResponse ? (
              <Response token={token} />
            ) : (
              []
            )}
            {mission?.Pieces?.length ? <Repertoire token={token} /> : []}
            {mission?.Engagements?.length ? <Schedule token={token} /> : []}
            {mission?.rosters().length ? <ProjectRosters token={token} /> : []}
            {mission?.Links?.length ? <Links token={token} /> : []}
            {mission?.DressCode?.length ? <DressCode token={token} /> : []}
            {mission?.Wage || mission?.WageDescription || mission?.WageBreakdown ? (
          <Wage token={token} />
        ) : (
          []
        )}
            {mission?.Contacts?.length ? <Contacts token={token} /> : []}
            {mission?.ProjectNotes ? <ProjectNotes token={token} /> : []}
            {/* {getMobileOperatingSystem() ? <DownloadApp /> : []} */}
          </>
        )}
      </Box>
      {/* GitBook-style "On this page" rail beside the column. Standalone
          page only: in embedded drawers its position:fixed would escape the
          drawer. It hides itself on viewports without room for a rail. */}
      {theme.modern && !hideResponse && !hideLogo && !hideFooter ? (
        <CallPageToc mission={mission} theme={theme} />
      ) : null}
      {!hideFooter && !theme.modern ? <Footer token={token} /> : []}
      {selectedInteractor &&
      !selectedInteractorJobStageID &&
      mainToken[0] !== "G" ? (
        <Box
          style={{
            position: "fixed",
            bottom: 0,
            zIndex: 2000,
            width: "100vw",
            padding: "16px",
            background: selectedInteractor.Color,
            color: interactorLabelColor(mission, selectedInteractor),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              level="body-lg"
              sx={{ color: "inherit", fontWeight: 700 }}
            >
              Please confirm your response
            </Typography>
            {mission.IsCattleCall &&
            mission.CattleCallSection &&
            isAcceptInteractor(selectedInteractor) ? (
              // Last look before the race is run: the instrument on offer.
              <Typography level="body-md" sx={{ color: "inherit" }}>
                This rush call is for <b>{mission.CattleCallSection}</b>.
              </Typography>
            ) : null}
            {isAcceptInteractor(selectedInteractor) && holdInteractor ? (
              // Spell out what Accept commits to, and make the alternative one
              // tap away rather than a Cancel-and-scroll-back-up.
              <Typography level="body-md" sx={{ color: "inherit" }}>
                {selectedInteractor.Name} only if you can do all the dates,{" "}
                <Chip
                  component="span"
                  size="sm"
                  variant="plain"
                  onClick={() =>
                    dispatch(setSelectedInteractor(holdInteractor))
                  }
                  startDecorator={
                    holdInteractor.Icon ? (
                      <i className={solidIcon(holdInteractor.Icon)} />
                    ) : undefined
                  }
                  sx={{
                    mx: 0.25,
                    // White text on a translucent white fill, so the pill picks
                    // up whatever colour the bar is. The fill is kept low on
                    // purpose: every extra percent of white lightens the
                    // backdrop and costs white-text contrast (0.18 reads
                    // 2.3:1 on green, 0.9 would be 1.1:1). The border does the
                    // work of making it look like a chip instead.
                    background: "rgba(255,255,255,0.18)",
                    fontWeight: 700,
                    "--Chip-paddingInline": "8px",
                    "--Chip-minHeight": "22px",
                    "&, & .MuiChip-label, & .MuiChip-startDecorator": {
                      color: "white",
                    },
                    // A clickable Joy Chip lays an absolutely positioned
                    // .MuiChip-action over the whole pill, and its variant
                    // background is opaque — it hides whatever the root is set
                    // to. The fill has to live on the root with this layer kept
                    // clear, or the chip is just Joy's grey.
                    "& .MuiChip-action": {
                      background: "transparent",
                      "&:hover": { background: "rgba(255,255,255,0.16)" },
                      "&:active": { background: "rgba(255,255,255,0.24)" },
                    },
                  }}
                >
                  {holdInteractor.Name}
                </Chip>{" "}
                with a message otherwise.
              </Typography>
            ) : (
              <Typography
                level="body-md"
                sx={{ color: "inherit", opacity: 0.58 }}
              >
                {selectedInteractor.Description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              color="neutral"
              sx={{
                color: "inherit",
                "&:hover": { background: "rgba(255,255,255,0.1)" },
                "&:active": { background: "rgba(255,255,255,0.2)" },
              }}
              onClick={() => {
                dispatch(setSelectedInteractor());
                dispatch(setSelectedInteractorJobStageID());
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              style={{
                flex: 1,
                marginLeft: 4,
                color: selectedInteractor.Color,
                background: "white",
                textTransform: "none",
              }}
              startDecorator={
                selectedInteractor?.Icon ? (
                  <i className={selectedInteractor.Icon}></i>
                ) : undefined
              }
              onClick={() => confirm(selectedInteractor)}
            >
              {selectedInteractor.Name}
            </Button>
          </Box>
        </Box>
      ) : (
        []
      )}
      {selectedInteractor &&
      !selectedInteractorJobStageID &&
      mainToken[0] !== "G" ? (
        <div
          style={{
            position: "absolute",
            background: "rgba(0,0,0,0.6)",
            height: "100%",
            width: "100%",
            top: 0,
            zIndex: 999,
          }}
          onClick={() => dispatch(setSelectedInteractor())}
        />
      ) : (
        []
      )}
      {loading ? (
        <Box
          style={{
            position: "absolute",
            background: "rgba(0,0,0,0.6)",
            height: "100vh",
            width: "100%",
            top: 0,
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(10px)",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        []
      )}
      <PartialAcceptWarning
        accept={partialAccept}
        hold={holdInteractor}
        companyName={mission?.CompanyName}
        onHold={() => {
          setPartialAccept(undefined);
          if (holdInteractor) handleSubmit(holdInteractor);
        }}
        onAccept={() => {
          const accept = partialAccept;
          setPartialAccept(undefined);
          if (accept) handleSubmit(accept);
        }}
        onEdit={() => setPartialAccept(undefined)}
      />
    </Box>
  );
}
